import { GmailRecipient, parseFromHeader, parseRecipients } from "./gmailAddresses";
export type { GmailRecipient } from "./gmailAddresses";

export interface GmailMessage {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  fromName: string;
  snippet: string;
  date: string;
  isUnread: boolean;
}

/**
 * A Gmail conversation as shown in the inbox list — one entry per
 * threadId, derived from the inbox messages we fetched. The
 * `from`/`fromName`/`subject`/`snippet`/`date` fields mirror the
 * latest message in the thread so the list view can render a single
 * row per conversation. `messages` contains every inbox-labelled
 * message in the thread (newest first) so we can archive the whole
 * conversation in one go.
 */
export interface GmailThread {
  id: string;
  from: string;
  fromName: string;
  subject: string;
  snippet: string;
  date: string;
  isUnread: boolean;
  messages: GmailMessage[];
  messageCount: number;
}

/**
 * A single message inside a thread detail fetch. Carries the body
 * inline so the conversation view can render every message without
 * a follow-up request per message.
 */
export interface GmailThreadMessage {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  fromName: string;
  /** Every address in the `To` header, in header order. */
  to: GmailRecipient[];
  date: string;
  isUnread: boolean;
  isInInbox: boolean;
  body: GmailMessageBody;
}

/**
 * Result of `threads.get?format=full` — every message in the thread
 * (including ones currently outside the inbox), sorted oldest first
 * to mirror Gmail's expanded-conversation order.
 */
export interface GmailThreadDetail {
  id: string;
  messages: GmailThreadMessage[];
}

const GMAIL_BASE = "https://gmail.googleapis.com/gmail/v1/users/me";

async function gmailFetch(
  endpoint: string,
  accessToken: string,
  options: RequestInit = {},
): Promise<Response> {
  return fetch(`${GMAIL_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    },
  });
}

function parseHeader(
  headers: { name: string; value: string }[],
  name: string,
): string {
  return (
    headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value ||
    ""
  );
}

async function fetchMessageWithRetry(
  id: string,
  accessToken: string,
  retries = 3,
): Promise<Record<string, unknown> | null> {
  for (let attempt = 0; attempt < retries; attempt++) {
    const res = await gmailFetch(
      `/messages/${id}?format=metadata&metadataHeaders=Subject&metadataHeaders=From&metadataHeaders=Date`,
      accessToken,
    );
    if (res.ok) return res.json();
    if (res.status === 429 && attempt < retries - 1) {
      await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      continue;
    }
    return null;
  }
  return null;
}

export async function fetchGmailMessages(
  accessToken: string,
  maxResults = 200,
): Promise<GmailMessage[]> {
  const listResponse = await gmailFetch(
    `/messages?labelIds=INBOX&maxResults=${maxResults}`,
    accessToken,
  );

  if (!listResponse.ok) {
    throw new Error(`Gmail API error: ${listResponse.status}`);
  }

  const listData = await listResponse.json();
  const messageIds: string[] = (listData.messages || []).map(
    (m: { id: string }) => m.id,
  );

  if (messageIds.length === 0) return [];

  // Fetch metadata in small concurrent batches to avoid Gmail 429 rate limits
  const batchSize = 10;
  const messages: GmailMessage[] = [];

  for (let i = 0; i < messageIds.length; i += batchSize) {
    const batch = messageIds.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map((id) => fetchMessageWithRetry(id, accessToken)),
    );

    for (const msg of batchResults) {
      if (!msg) continue;
      const payload = msg.payload as { headers?: { name: string; value: string }[] } | undefined;
      const headers = payload?.headers || [];
      const subject = parseHeader(headers, "Subject") || "(no subject)";
      const fromRaw = parseHeader(headers, "From");
      const { name: fromName, email: from } = parseFromHeader(fromRaw);
      const date = parseHeader(headers, "Date");
      const msgLabelIds = msg.labelIds as string[] | undefined;
      const isUnread = (msgLabelIds || []).includes("UNREAD");

      messages.push({
        id: msg.id as string,
        threadId: msg.threadId as string,
        subject,
        from,
        fromName,
        snippet: (msg.snippet as string) || "",
        date,
        isUnread,
      });
    }
  }

  return messages;
}

export async function archiveGmailMessage(
  accessToken: string,
  messageId: string,
  { keepalive = false }: { keepalive?: boolean } = {},
): Promise<void> {
  const response = await gmailFetch(
    `/messages/${messageId}/modify`,
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({ removeLabelIds: ["INBOX"] }),
      // `keepalive: true` lets the request survive page unload so pending
      // archives flushed during a refresh still reach Gmail.
      keepalive,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to archive message: ${response.status}`);
  }
}

// Gmail's batchModify applies the same label change to up to 1000 messages
// in a single request, so N emails cost 1 API call instead of N — avoiding
// the per-user concurrent request cap (RESOURCE_EXHAUSTED / 429) that a
// parallel fan-out of modify calls trips on larger selections.
export async function archiveGmailMessages(
  accessToken: string,
  messageIds: string[],
  { keepalive = false }: { keepalive?: boolean } = {},
): Promise<void> {
  if (messageIds.length === 0) return;
  const response = await gmailFetch(
    `/messages/batchModify`,
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({
        ids: messageIds,
        removeLabelIds: ["INBOX"],
      }),
      keepalive,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to archive messages: ${response.status}`);
  }
}

// `labels.get` returns messagesTotal for the inbox without listing
// individual messages, so it's the cheapest way to get an accurate inbox
// count for the Emails button badge (no cap at `maxResults` like list does).
export async function fetchGmailInboxCount(
  accessToken: string,
): Promise<number> {
  const response = await gmailFetch("/labels/INBOX", accessToken);
  if (!response.ok) {
    throw new Error(`Gmail API error: ${response.status}`);
  }
  const data = (await response.json()) as { messagesTotal?: number };
  return data.messagesTotal ?? 0;
}

// Gmail's `/u/<N>/` path segment uses login order (unstable), and it
// rejects an email in that slot with a 404. The `authuser` query param is
// Google's documented cross-product account selector: it picks the matching
// signed-in session by email, or prompts sign-in if none matches.
export function getGmailMessageUrl(
  messageId: string,
  accountEmail?: string | null,
): string {
  if (accountEmail) {
    return `https://mail.google.com/mail/?authuser=${encodeURIComponent(accountEmail)}#all/${messageId}`;
  }
  return `https://mail.google.com/mail/#all/${messageId}`;
}

export interface GmailAttachment {
  attachmentId: string;
  filename: string;
  mimeType: string;
  size: number;
}

export interface GmailMessageBody {
  html: string | null;
  text: string | null;
  attachments: GmailAttachment[];
}

interface GmailPayloadNode {
  mimeType?: string;
  filename?: string;
  body?: { data?: string; attachmentId?: string; size?: number };
  parts?: GmailPayloadNode[];
}

/**
 * Decode a base64url string (as used by the Gmail API) into a UTF-8 string.
 * Gmail uses `-` and `_` instead of `+` and `/`, and omits padding.
 */
function base64UrlDecode(data: string): string {
  const base64 = data.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "===".slice((base64.length + 3) % 4);
  const binary = atob(padded);
  // Reinterpret the raw byte string as UTF-8.
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder("utf-8").decode(bytes);
}

/**
 * Walk a Gmail message payload tree and return the first text/html and
 * text/plain bodies found. Either (or both) may be null if the message
 * doesn't include that part.
 */
function extractMessageBody(
  payload: GmailPayloadNode,
): { html: string | null; text: string | null } {
  let html: string | null = null;
  let text: string | null = null;

  const walk = (part: GmailPayloadNode): void => {
    const mime = part.mimeType?.toLowerCase();
    const data = part.body?.data;
    if (data) {
      if (mime === "text/html" && !html) {
        html = base64UrlDecode(data);
      } else if (mime === "text/plain" && !text) {
        text = base64UrlDecode(data);
      }
    }
    if (part.parts) {
      for (const sub of part.parts) walk(sub);
    }
  };

  walk(payload);
  return { html, text };
}

/**
 * Walk a Gmail message payload tree and collect attachment metadata.
 * A part is an attachment when it has a non-empty filename and an
 * attachmentId (meaning its content is stored separately and must
 * be fetched with an additional API call).
 */
function extractAttachments(payload: GmailPayloadNode): GmailAttachment[] {
  const attachments: GmailAttachment[] = [];

  const walk = (part: GmailPayloadNode): void => {
    if (part.filename && part.body?.attachmentId) {
      attachments.push({
        attachmentId: part.body.attachmentId,
        filename: part.filename,
        mimeType: part.mimeType || "application/octet-stream",
        size: part.body.size || 0,
      });
    }
    if (part.parts) {
      for (const sub of part.parts) walk(sub);
    }
  };

  walk(payload);
  return attachments;
}

/**
 * Decode a base64url string into raw bytes (Uint8Array).
 */
function base64UrlDecodeToBytes(data: string): Uint8Array {
  const base64 = data.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "===".slice((base64.length + 3) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Fetch the full body of a single Gmail message. Returns the text/html
 * and text/plain parts; callers can pick whichever they want to render.
 */
export async function fetchGmailMessageBody(
  accessToken: string,
  messageId: string,
): Promise<GmailMessageBody> {
  const res = await gmailFetch(
    `/messages/${messageId}?format=full`,
    accessToken,
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch message body: ${res.status}`);
  }
  const data = (await res.json()) as { payload?: GmailPayloadNode };
  if (!data.payload) return { html: null, text: null, attachments: [] };
  const body = extractMessageBody(data.payload);
  const attachments = extractAttachments(data.payload);
  return { ...body, attachments };
}

/**
 * Group a flat list of Gmail messages into one entry per conversation.
 * The latest message in each thread becomes the thread's representative
 * row (subject, sender, snippet, date) so the inbox list can show one
 * row per conversation, matching Gmail's behaviour.
 */
export function groupMessagesIntoThreads(
  messages: GmailMessage[],
): GmailThread[] {
  const byThread = new Map<string, GmailMessage[]>();
  for (const msg of messages) {
    const arr = byThread.get(msg.threadId);
    if (arr) arr.push(msg);
    else byThread.set(msg.threadId, [msg]);
  }

  const threads: GmailThread[] = [];
  for (const [threadId, msgs] of byThread) {
    msgs.sort((a, b) => {
      const ta = new Date(a.date).getTime() || 0;
      const tb = new Date(b.date).getTime() || 0;
      return tb - ta; // newest first
    });
    const latest = msgs[0];
    threads.push({
      id: threadId,
      from: latest.from,
      fromName: latest.fromName,
      subject: latest.subject,
      snippet: latest.snippet,
      date: latest.date,
      isUnread: msgs.some((m) => m.isUnread),
      messages: msgs,
      messageCount: msgs.length,
    });
  }

  threads.sort((a, b) => {
    const ta = new Date(a.date).getTime() || 0;
    const tb = new Date(b.date).getTime() || 0;
    return tb - ta;
  });

  return threads;
}

/**
 * Fetch every message in a Gmail thread, including ones that are no
 * longer in the inbox. Bodies are decoded inline so the conversation
 * view can render the whole thread without per-message follow-ups.
 */
export async function fetchGmailThread(
  accessToken: string,
  threadId: string,
): Promise<GmailThreadDetail> {
  const res = await gmailFetch(
    `/threads/${threadId}?format=full`,
    accessToken,
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch thread: ${res.status}`);
  }
  const data = (await res.json()) as {
    messages?: {
      id: string;
      threadId: string;
      snippet?: string;
      labelIds?: string[];
      payload?: GmailPayloadNode & {
        headers?: { name: string; value: string }[];
      };
    }[];
  };

  const messages: GmailThreadMessage[] = (data.messages || []).map((msg) => {
    const headers = msg.payload?.headers || [];
    const subject = parseHeader(headers, "Subject") || "(no subject)";
    const fromRaw = parseHeader(headers, "From");
    const { name: fromName, email: from } = parseFromHeader(fromRaw);
    const to = parseRecipients(parseHeader(headers, "To"));
    const date = parseHeader(headers, "Date");
    const labelIds = msg.labelIds || [];
    const isUnread = labelIds.includes("UNREAD");
    const isInInbox = labelIds.includes("INBOX");

    const body: GmailMessageBody = msg.payload
      ? {
          ...extractMessageBody(msg.payload),
          attachments: extractAttachments(msg.payload),
        }
      : { html: null, text: null, attachments: [] };

    return {
      id: msg.id,
      threadId: msg.threadId,
      subject,
      from,
      fromName,
      to,
      date,
      isUnread,
      isInInbox,
      body,
    };
  });

  // Newest first — matches the rest of JuDI (sender list, inbox)
  // and surfaces the most recent reply at the top of the conversation.
  messages.sort((a, b) => {
    const ta = new Date(a.date).getTime() || 0;
    const tb = new Date(b.date).getTime() || 0;
    return tb - ta;
  });

  return { id: threadId, messages };
}

/**
 * Download the raw bytes of a single attachment from a Gmail message.
 */
export async function fetchGmailAttachment(
  accessToken: string,
  messageId: string,
  attachmentId: string,
): Promise<Uint8Array> {
  const res = await gmailFetch(
    `/messages/${messageId}/attachments/${attachmentId}`,
    accessToken,
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch attachment: ${res.status}`);
  }
  const json = (await res.json()) as { data: string };
  return base64UrlDecodeToBytes(json.data);
}
